/**
 * Produire le document d'une facture et l'envoyer — la règle, sans le transport.
 *
 * ## Pourquoi ce fichier existe
 *
 * Cette étape vivait DANS la page de confirmation du parent, et nulle part
 * ailleurs. L'écran d'inscription de l'administration passe pourtant par le
 * même `commander` : il créait la facture et les inscriptions, puis affichait
 * une modale de succès — sans document ni courriel. Trois factures sont
 * restées sans PDF, et rien ne le disait.
 *
 * Une étape écrite dans un écran est une étape que le prochain écran oubliera.
 * Elle est donc ici, et les deux tunnels l'appellent.
 *
 * ## Pourquoi elle est séparée de `gateway.supabase.ts`
 *
 * Ce module-là importe le client Supabase, qui **lève au chargement** quand
 * les variables d'environnement manquent : rien de ce qu'il contient n'est
 * éprouvable. La règle, elle, n'a besoin d'aucun réseau — elle reçoit ses deux
 * appels et décide. C'est le même partage que `devis.ts` (la règle) et la
 * passerelle (le transport).
 *
 * ## Pourquoi elle n'est PAS dans `commander`
 *
 * Parce qu'un parent ne doit pas attendre un envoi SMTP devant son bouton
 * « Valider ». Le tunnel parent commande, navigue vers sa page de confirmation
 * — qui lui dit tout de suite que son enfant est inscrit — et produit le
 * document ensuite. L'administration, elle, attend : c'est un poste de
 * travail, et savoir que le courriel est parti fait partie du geste.
 */

/**
 * Ce que l'envoi a donné.
 *
 * **Un échec n'annule rien.** L'enfant est inscrit, la facture existe. Le
 * résultat dit ce qui a marché pour que l'écran le montre honnêtement plutôt
 * que de laisser croire qu'un courriel est parti.
 */
export interface EnvoiDeFacture {
  pdfUrl: string | null;
  /** Le message d'échec, ou `null` si tout est parti. */
  erreur: string | null;
}

/** Les deux appels que cette étape enchaîne. */
export interface OutilsDeFacture {
  /** L'adresse du PDF déjà produit pour cette facture, s'il existe. */
  lirePdf: (invoiceNumber: string) => Promise<string | null>;
  /**
   * Produire le document et l'envoyer à cette adresse.
   *
   * `forcer` voyage jusqu'au bout : le verrou d'envoi vit désormais en base
   * (`claim_invoice_email`), et lui seul sépare les deux émetteurs — la page du
   * parent et le webhook Stripe. Le garde-fou de ce module ne suffit donc plus
   * à décrire l'intention ; il faut la transmettre.
   */
  envoyer: (
    invoiceNumber: string, courriel: string, forcer: boolean,
  ) => Promise<EnvoiDeFacture>;
}

export interface OptionsDeFacture {
  /**
   * Envoyer même si un PDF existe déjà.
   *
   * Le bouton « renvoyer » de l'écran des paiements : un geste délibéré, par
   * opposition à un parent qui rafraîchit sa page.
   */
  forcer?: boolean;
}

export async function produireEtEnvoyer(
  invoiceNumber: string,
  courriel: string | null,
  outils: OutilsDeFacture,
  options: OptionsDeFacture = {},
): Promise<EnvoiDeFacture> {
  if (!invoiceNumber) return { pdfUrl: null, erreur: 'Aucun numéro de facture.' };
  if (!courriel) {
    return { pdfUrl: null, erreur: "Aucune adresse : la facture n'a pas été envoyée." };
  }

  /*
    Le garde-fou tient ENTRE deux visites, parce qu'il lit `pdf_url` sur la
    facture et non une variable de page : un parent qui rafraîchit ne reçoit
    pas un deuxième courriel.
  */
  if (!options.forcer) {
    const deja = await outils.lirePdf(invoiceNumber);
    if (deja) return { pdfUrl: deja, erreur: null };
  }

  return outils.envoyer(invoiceNumber, courriel, options.forcer === true);
}
